import { useContext, useEffect, useState } from 'react';
import {
  Button,
  Stack,
  TextField,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useForm, Controller } from 'react-hook-form';
import type { OverridableStringUnion } from '@mui/types';

import PageBaseLayout from '../layouts/PageBaseLayout';
import { supabase } from '../supabase-client';
import useDialog from '../hooks/useDialog';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import { AuthContext } from '../contexts/AuthContext';

type FormValues = {
  new_password: string;
};

const defaultValues: FormValues = {
  new_password: '',
};

export default function Account() {
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const authCtx = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const {
    handleSubmit,
    control,
    reset,
    resetField,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
  });

  const onSubmit = async (data: FormValues) => {
    backdrop.handleBackdropOpen();
    console.log(data);

    let message = `Your password has been updated.`;
    let severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    try {
      const response = await supabase.auth.updateUser({
        password: data.new_password,
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      reset();
      dialog.handleDialogClose();
    } catch (error) {
      message = String(error);
      severity = 'error';
    }
    snackbar.handleSnackbarOpen(message, severity);
    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    supabase.auth.getUser().then((result) => {
      setUserEmail(result.data.user?.email || '');
    });
  }, []);

  return (
    <PageBaseLayout title="Account">
      <Stack component="form" spacing={3} onSubmit={handleSubmit(onSubmit)}>
        <TextField
          disabled
          type="text"
          label="Email"
          value={userEmail}
          fullWidth
        />
        <Controller
          name="new_password"
          control={control}
          rules={{ required: 'Password is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              slotProps={{
                input: {
                  endAdornment: showPassword ? (
                    <VisibilityIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setShowPassword(false)}
                    />
                  ) : (
                    <VisibilityOffIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setShowPassword(true)}
                    />
                  ),
                },
              }}
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              error={!!errors.new_password}
              helperText={errors.new_password?.message}
              required
              fullWidth
            />
          )}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting}>
          Change Password
        </Button>
      </Stack>
    </PageBaseLayout>
  );
}
